import * as React from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { Controller, Control, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { CustomLabel } from "../input/CustomLabel";
import { InputErrorMessage } from "../input/InputErrorMessage";
import { DirecoesCalendario } from "types/inputs/DatePicker/DirecoesCalendario";
import { ptBR } from "date-fns/locale";

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const customPtBrLocale = {
  ...ptBR,
  localize: {
    ...ptBR.localize,
    month: (monthIndex: number, options?: { width?: "abbreviated" | "narrow" | "wide" }) => {
      const originalMonth = ptBR.localize?.month(monthIndex, options);
      return capitalize(originalMonth);
    },
  },
} as Locale;

type MesCompetenciaWithoutValidationProps = {
  usarValidacaoParaFormulario: false;
};

type MesCompetenciaWithValidationProps = {
  usarValidacaoParaFormulario: true;
  /**Nome do atributo que será usado no formulario para realizar as validacoes*/
  name: string;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  register: UseFormRegister<any>;
};

type MesCompetenciaProps = {
  label?: string;
  disabled?: boolean;
  disabledFuture?: boolean;
  disabledPast?: boolean;
  disabledTextField?: boolean;
  onChange?: (dataSelecionada: Date | null) => void;
  defaultValue?: Date | null;
  variant?: "standard" | "outlined" | "filled";
  style?: object;
  value?: Date | null;
  popperDirection?: DirecoesCalendario;
  exibirMesPorExtenso?: boolean;
} & (MesCompetenciaWithoutValidationProps | MesCompetenciaWithValidationProps);

export const MesCompetencia: React.FC<MesCompetenciaProps> = (props) => {
  const formatoData = props.exibirMesPorExtenso ? "LLLL/yyyy" : "MM/yyyy";
  const MIN_DATE = new Date(1901, 0, 1);

  const renderMesCompetenciaComValidacao = (): React.ReactNode => {
    if (props.usarValidacaoParaFormulario) {
      return (
        <Controller
          name={props.name}
          control={props.control}
          defaultValue={props.defaultValue}
          render={({ field, fieldState }) => (
            <>
              {props.label && <CustomLabel possuiErro={fieldState.error != null}>{props.label}</CustomLabel>}
              <DatePicker
                {...props.register(props.name)}
                {...field}
                value={props.value}
                openTo="month"
                defaultValue={props.defaultValue}
                formatDensity="spacious"
                format={formatoData}
                views={["year", "month"]}
                disabled={props.disabled}
                disableFuture={props.disabledFuture}
                disablePast={props.disabledPast}
                minDate={MIN_DATE}
                slotProps={{
                  textField: {
                    variant: props.variant || "outlined",
                    color: "info",
                    size: "small",
                    sx: props.style,
                    label: "",
                    error: fieldState.error != null,
                    disabled: props.disabledTextField,
                  },
                  popper: {
                    placement: props.popperDirection || "bottom-start",
                    style: { zIndex: 99999 },
                  },
                }}
                onChange={(dataSelecionada: Date | null) => {
                  field.onChange(dataSelecionada);
                  props.setValue(props.name, dataSelecionada);
                  props.onChange && props.onChange(dataSelecionada);
                }}
              />
              {fieldState.error && <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>}
            </>
          )}
        />
      );
    }
  };

  const renderMesCompetenciaSemValidacao = (): React.ReactNode => {
    if (!props.usarValidacaoParaFormulario) {
      return (
        <>
          {props.label && <CustomLabel possuiErro={false}>{props.label}</CustomLabel>}
          <DatePicker
            defaultValue={props.defaultValue}
            value={props.value}
            openTo="month"
            onChange={(data: Date | null) => props.onChange && props.onChange(data)}
            formatDensity="spacious"
            format={formatoData}
            views={["year", "month"]}
            disabled={props.disabled}
            disableFuture={props.disabledFuture}
            disablePast={props.disabledPast}
            minDate={MIN_DATE}
            slotProps={{
              textField: {
                variant: props.variant || "outlined",
                color: "info",
                size: "small",
                sx: props.style,
                label: "",
                disabled: props.disabledTextField,
                inputProps: {
                  style: { textAlign: "right" },
                },
              },
              popper: {
                placement: props.popperDirection || "bottom-start",
                style: { zIndex: 99999 },
              },
            }}
          />
        </>
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={customPtBrLocale}>
      {props.usarValidacaoParaFormulario && renderMesCompetenciaComValidacao()}
      {!props.usarValidacaoParaFormulario && renderMesCompetenciaSemValidacao()}
    </LocalizationProvider>
  );
};
